/**
 * The three playable factions.
 *
 * Every match is a three-way fight, so "friendly / enemy" is never enough:
 * a marker has to say *which* enemy. Picking your faction also recolours the
 * whole interface, which is the fastest possible way to answer "is that mine"
 * at a glance on a second screen.
 *
 * Descriptions are from BULKHEAD's own FAQ.
 *
 * Colours deliberately match the convention the existing community tools
 * already use — Valkyra red, Manticore green, Lonestar blue. Being internally
 * prettier is worth far less than a player reading our map the same way they
 * read the two they already have open. Hues are kept, but lightness and chroma
 * are tuned for a dark second screen.
 *
 * Red and green together are the worst possible pair for deuteranopia, so
 * colour is never the only channel: every faction also carries a distinct
 * marker shape and its initial.
 */

export type FactionId = "lonestar" | "valkyra" | "manticore";

export interface Faction {
  readonly id: FactionId;
  readonly label: string;
  readonly blurb: string;
  /** Base colour, used for markers and accents. */
  readonly color: string;
  /** A dimmed variant for fills and inactive states. */
  readonly colorMuted: string;
  /** Foreground colour that stays readable on top of `color`. */
  readonly onColor: string;
  /**
   * Non-colour identity channel, so the map stays readable without hue.
   * Rendered as the marker outline shape.
   */
  readonly shape: "circle" | "square" | "diamond";
  readonly initial: string;
}

export const FACTIONS: readonly Faction[] = [
  {
    id: "lonestar",
    label: "Lonestar",
    blurb: "The heavy-hitters in the Western paramilitary world.",
    color: "oklch(0.7 0.13 240)",
    colorMuted: "oklch(0.7 0.13 240 / 0.2)",
    onColor: "oklch(0.16 0.02 240)",
    shape: "square",
    initial: "L",
  },
  {
    id: "valkyra",
    label: "Valkyra",
    blurb: "Aims to return the Soviet People's Republic to greatness.",
    color: "oklch(0.66 0.16 25)",
    colorMuted: "oklch(0.66 0.16 25 / 0.2)",
    onColor: "oklch(0.97 0.01 25)",
    shape: "diamond",
    initial: "V",
  },
  {
    id: "manticore",
    label: "Manticore",
    blurb: "The Kingdom of Persia, Tehran's shadow army.",
    color: "oklch(0.76 0.13 155)",
    colorMuted: "oklch(0.76 0.13 155 / 0.2)",
    onColor: "oklch(0.16 0.02 155)",
    shape: "circle",
    initial: "M",
  },
];

const FACTIONS_BY_ID = new Map<FactionId, Faction>(
  FACTIONS.map((faction) => [faction.id, faction]),
);

export function getFaction(id: FactionId): Faction {
  const faction = FACTIONS_BY_ID.get(id);
  if (!faction) throw new Error(`Unknown faction: ${id}`);
  return faction;
}

export function isFactionId(value: string): value is FactionId {
  return FACTIONS_BY_ID.has(value as FactionId);
}
